import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import {
  documentStatusChip,
  formatFileSize,
  isPreviewable,
  moduleLabel,
} from "./clientDocuments.utils";

const formatUploadedAt = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm");
  } catch {
    return "-";
  }
};

const DocumentFileRow = ({
  document,
  onPreview,
  onDownload,
  onDelete,
  onShowInModule,
  moduleLinks = [],
  busy,
  canDelete = false,
  canLink = false,
  deletable = false,
  requestStatus,
}) => {
  const { label, color } = documentStatusChip(document, requestStatus);
  const previewable = isPreviewable(document);
  const moduleLinkCount = moduleLinks.length;

  return (
    <Box className="flex items-center justify-between gap-3 py-2 border-b border-app-border last:border-b-0">
      <Box className="min-w-0">
        <Typography variant="body2" className="truncate">
          {document.originalFilename || "Untitled file"}
        </Typography>
        <Typography variant="caption" className="text-app-text-muted">
          {formatFileSize(document.sizeBytes)} - {formatUploadedAt(document.uploadedAt)}
        </Typography>
      </Box>
      <Box className="flex items-center gap-1 shrink-0">
        <Chip size="small" label={label} color={color} />
        {document.source === "PARTNER_UPLOAD" && (
          <Chip size="small" variant="outlined" color="info" label="Uploaded by partner" />
        )}
        <Tooltip title={previewable ? "Preview" : "This file type cannot be previewed"}>
          <span>
            <IconButton
              size="small"
              disabled={!previewable || busy}
              onClick={() => onPreview(document)}
              aria-label={`Preview ${document.originalFilename || "file"}`}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => onDownload(document)}
              aria-label={`Download ${document.originalFilename || "file"}`}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {canLink && deletable && (
          <Tooltip
            title={
              moduleLinkCount > 0 ? (
                <Box component="span">
                  <Typography variant="caption" component="div" className="font-semibold">
                    Shown in:
                  </Typography>
                  {moduleLinks.map((link) => (
                    <Typography key={link.id} variant="caption" component="div">
                      {moduleLabel(link.moduleType)}
                      {link.entryLabel ? ` · ${link.entryLabel}` : ""}
                    </Typography>
                  ))}
                </Box>
              ) : (
                "Show in module"
              )
            }
          >
            <span>
              <IconButton
                size="small"
                color={moduleLinkCount > 0 ? "primary" : "default"}
                disabled={busy}
                onClick={() => onShowInModule(document)}
                aria-label={`Show ${document.originalFilename || "file"} in a module`}
              >
                <AccountTreeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canDelete && deletable && (
          <Tooltip title="Delete this file">
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={busy}
                onClick={() => onDelete(document)}
                aria-label={`Delete ${document.originalFilename || "file"}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default DocumentFileRow;
